import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { ProjectVersionApi, ProjectVersionInterface } from '@rappider/api-sdk';
import { BreadcrumbOption, ButtonComponentConfig, ButtonType, HeadingComponentConfig, SpinOpacity } from '@rappider/rappider-components/utils';
import { ProjectWithRelations } from '@rappider/rappider-sdk';
import { RouterStateService, StoreKeys, StringUtilityService, SyncRemoteDataService } from '@rappider/services';
import { LAST_PROJECT_EXPORT_LIST_CONFIG, PROJECT_EXPORT_LIST_CONFIG } from '@rappider/shared/configs';
import { defaultToolbarTitleHeadingSize, IconType, PATH_DEFINITIONS } from '@rappider/shared/definitions';
import { combineLatest, interval, Subject, Subscription, takeUntil } from 'rxjs';
import { GetProjectVersions } from '../../states/project-versions-state/project-version.actions';
import { orderBy } from 'lodash';
import { FilterGenerateCodeIds } from 'libs/project-source-code/src/lib/project-source-code-data-source/project-source-code.actions';

@Component({
  selector: 'rappider-project-version-detail',
  templateUrl: './project-version-detail.component.html',
  styleUrls: ['./project-version-detail.component.scss']
})
export class ProjectVersionDetailComponent implements OnInit, OnDestroy {

  /* exported version list grid configs */
  PROJECT_EXPORT_LIST_CONFIG = PROJECT_EXPORT_LIST_CONFIG;
  LAST_PROJECT_EXPORT_LIST_CONFIG = LAST_PROJECT_EXPORT_LIST_CONFIG;
  /* main title */
  mainTitle: HeadingComponentConfig;
  /* component title */
  title: string | string[] | BreadcrumbOption[];
  /* active project data */
  activeProject: ProjectWithRelations;
  /* project version informations */
  projectVersionId: string; /* from url */
  projectVersion: ProjectVersionInterface; /* getting project version by id */
  projectVersions: ProjectVersionInterface[];
  /* latest project export */
  latestProjectExport: any | ProjectWithRelations;
  /* project export list */
  projectExports: any[];
  /* Subscriptions */
  subscriptions: Subscription[];
  /* button icon */
  projectExportPageButton: ButtonComponentConfig = {
    text: 'PROJECT_MODULE.PROJECT_VERSION_DETAIL_COMPONENT.EXPORT_PROJECT',
    type: ButtonType.Default,
    icon: {
      name: 'far fa-file-export'
    }
  };
  isExportDetailsCollapsed = false;
  exportDetailsTogglerButton = {
    text: 'PROJECT_MODULE.PROJECT_VERSION_DETAIL_COMPONENT.HIDE_EXPORT_DETAILS',
    type: ButtonType.Default,
    icon: {
      name: 'up',
      type: IconType.NgZorro
    }
  };
  displayToolbar = false;
  displayToolbarBackButton = false;
  exportStatusFlag = false;
  status: string;
  generateProjectLoading = false;
  generateCodeIds: string[];
  spinOpacity = SpinOpacity;

  // If there is no project version Id on the URL; we will set this flag so that we can navigate to the last project version
  invalidProjectVersionIdOnURL = false;
  private pollingProjectVersion$ = new Subject<void>();

  constructor(
    private store: Store<any>,
    private activatedRoute: ActivatedRoute,
    private projectVersionApi: ProjectVersionApi,
    private routerStateService: RouterStateService,
    private syncRemoteDataService: SyncRemoteDataService
  ) { }

  ngOnInit() {
    this.subscriptions = [];
    this.getProjectVersionIdFromURL();

    interval(5000).pipe(
      takeUntil(this.pollingProjectVersion$)
    ).subscribe(() => {
      if (this.activeProject?.id) {
        this.syncRemoteDataService.syncRemoteDataItemWithKey(this.activeProject.id, StoreKeys.ProjectCodeGeneration);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.pollingProjectVersion$.next();
    this.pollingProjectVersion$.complete();
  }

  /* getting project version id from url params */
  getProjectVersionIdFromURL() {
    this.projectVersionId = this.activatedRoute.snapshot.params.projectVersionId;

    const paramsSub = this.activatedRoute.params.subscribe(params => {
      const newVersionId = params.projectVersionId;

      if (newVersionId) {
        this.projectVersionId = newVersionId;
        this.invalidProjectVersionIdOnURL = false;

        if (this.activeProject) {
          this.getProjectVersionById(this.projectVersionId);
        }
      } else {
        this.invalidProjectVersionIdOnURL = true;
      }
    });

    this.subscriptions.push(paramsSub);
    this.subscribeToData();
  }

  /**
   * Subscribe to all data
   *
   * @memberof ProjectVersionListComponent
   */
  subscribeToData() {
    this.subscriptions = [
      ...this.subscriptions,
      this.subscribeToActiveProject(),
      this.subscribeToProjectExportsData(),
      this.subscribeToProjectVersions(),
      this.subscribeToLoadingGenerateProjectIds()
    ];
  }

  /**
   * get project exports data field
   *
   * @param {string} projectId
   * @memberof ProjectVersionListComponent
   */
  subscribeToProjectExportsData() {
    return combineLatest([
      this.store.select(state => state.projectCodeGeneration?.data),
      this.activatedRoute.params
    ]).subscribe(([data, activeRoute]) => {
      if (!data) {
        this.resetExportsData();
        return;
      }

      const currentVersionId = activeRoute.projectVersionId;
      const filteredExports = data?.filter(projectExport =>
        projectExport.projectVersionId === currentVersionId
      );

      if (!filteredExports?.length) {
        this.resetExportsData();
        return;
      }

      this.processExports(filteredExports);
    });
  }

  private processExports(filteredExports: any[]) {
    this.projectExports = orderBy(filteredExports.map((exportData, index: number) => {
      const versionNumber = this.projectVersion?.versionNumber;
      const matchingVersion = this.projectVersions?.find(projectVersion =>
        projectVersion.id === exportData.projectVersionId);

      return {
        ...exportData,
        versionNumber: versionNumber || matchingVersion?.versionNumber || exportData.versionNumber,
        isLatestExport: index === 0, /* TODO: fix this field when pagination added to this component */
        appType: StringUtilityService.toPascalCase(this.activeProject.appType),
        frontendFramework: StringUtilityService.toPascalCase(this.activeProject.frontendFramework),
        backendFramework: StringUtilityService.toPascalCase(this.activeProject.backendFramework)
      };
    }), 'createdDate', 'desc');

    const filteredIds = this.generateCodeIds?.filter(generateCodeId =>
      (this.projectExports.map(projectExport => projectExport.id).includes(generateCodeId))
    );

    if (filteredIds?.length) {
      this.store.dispatch(FilterGenerateCodeIds({ payload: { generateCodeIds: filteredIds } }));
    }

    if (this.projectExports?.length) {
      this.latestProjectExport = {
        ...this.projectExports[0],
        versionNumber: this.projectVersion?.versionNumber || this.projectExports[0]?.versionNumber,
        completedStepCount: this.projectExports[0]?.status === 'successful' ? 4 :
          (this.projectExports[0]?.status === 'processing' ? 2 : 0),
        totalStepCount: 4,
        percent: this.projectExports[0]?.status === 'successful' ? 100 :
          (this.projectExports[0]?.status === 'processing' ? 50 : 0),
      };
    } else {
      this.resetExportsData();
    }
  }

  private resetExportsData() {
    this.projectExports = [];
    this.latestProjectExport = null;
  }

  /**
   * get active project from state and get project versions by active project id
   *
   * @return {*}
   * @memberof ProjectVersionListComponent
   */
  subscribeToActiveProject() {
    return this.store.select(state => state.activeProject.data).subscribe(activeProject => {
      this.activeProject = activeProject;
      if (activeProject) {
        this.getProjectVersions();
        if (this.projectVersionId) {
          this.getProjectVersionById(this.projectVersionId);
        }
      }
    });
  }

  /**
   * get project versions data from state
   *
   * @return {*}
 * @memberof ProjectVersionListComponent
   */
  subscribeToProjectVersions() {
    return this.store.select(state => state.projectVersion.data).subscribe((projectVersions: ProjectVersionInterface[]) => {
      if (projectVersions && projectVersions.length > 0) {
        this.projectVersions = projectVersions;
        this.getProjectVersion();
      }
    });
  }

  subscribeToLoadingGenerateProjectIds() {
    return this.store.select(state => state.projectSourceCode.generateCodeIds).subscribe((generateCodeIds: string[]) => {
      if (generateCodeIds?.length) {
        this.generateCodeIds = generateCodeIds;
        this.generateProjectLoading = !generateCodeIds.includes(this.latestProjectExport?.id);
      } else {
        this.generateProjectLoading = false;
      }
    });
  }

  /**
   * Get project version from version list
   *
   * @memberof ProjectVersionDetailComponent
   */
  getProjectVersion() {
    if (this.projectVersions && this.projectVersions.length && this.projectVersionId) {
      this.projectVersion = this.projectVersions.find(
        projectVersion => projectVersion.id === this.projectVersionId
      );

      if (this.projectVersion) {
        this.compareTheVersions();
      } else {
        this.invalidProjectVersionIdOnURL = true;
        this.navigateToLastVersion();
      }
    } else if (this.invalidProjectVersionIdOnURL && this.projectVersions && this.projectVersions.length) {
      this.navigateToLastVersion();
    }
  }

  /**
   * Navigate to the last version
   *
   * @memberof ProjectVersionDetailComponent
   */
  navigateToLastVersion() {
    const lastProjectVersion = this.getLastProjectVersion();
    if (!lastProjectVersion) {
      return;
    }

    this.projectVersion = lastProjectVersion;
    this.projectVersionId = lastProjectVersion.id;

    this.updateTitles();
    this.routerStateService.navigate(
      `${PATH_DEFINITIONS.PROJECTS.PROJECT_VERSION_DETAIL}/${this.projectVersionId}`
    );

    this.compareTheVersions();
  }

  compareTheVersions() {
    if (this.projectVersions && this.projectVersion) {
      const lastProjectVersion = this.getLastProjectVersion();
      if (lastProjectVersion.createdDate === this.projectVersion.createdDate) {
        this.exportStatusFlag = false;
      } else {
        this.exportStatusFlag = true;
      }
    } else {
      // sometimes version is undefined, so we should check this condition
      this.exportStatusFlag = true;
    }
  }

  getLastProjectVersion() {
    const sortedVersions = this.projectVersions.slice().sort((a, b) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    );
    return sortedVersions[0];
  }

  getProjectVersions() {
    this.store.dispatch(new GetProjectVersions());
  }

  /**
  /**
   * Get project version from API by id
   *
   * @param {string} projectVersionId
   * @memberof ProjectVersionDetailComponent
   */
  getProjectVersionById(projectVersionId: string) {
    if (!projectVersionId) {
      return;
    }

    this.projectVersionApi.findById(projectVersionId).subscribe((projectVersion: ProjectVersionInterface) => {
      if (projectVersion) {
        this.projectVersion = projectVersion;
        this.updateTitles();

        this.updateExportVersionNumbers();
      }
    });
  }

  private updateExportVersionNumbers() {
    if (!this.projectExports?.length || !this.projectVersion) {
      return;
    }

    this.projectExports.forEach(exportItem => {
      exportItem.versionNumber = this.projectVersion.versionNumber;
    });

    if (this.latestProjectExport) {
      this.latestProjectExport.versionNumber = this.projectVersion.versionNumber;
    }
  }

  /**
   * Update component titles
   *
   * @memberof ProjectVersionDetailComponent
   */
  updateTitles() {
    if (!this.activeProject || !this.projectVersion) {
      return;
    }

    this.mainTitle = {
      content: 'PROJECT_MODULE.PROJECT_VERSIONS_COMPONENT.PROJECT_VERSIONS',
      type: defaultToolbarTitleHeadingSize
    };
    this.title = [
      {
        label: this.activeProject.name,
        redirectUrl: `${PATH_DEFINITIONS.PROJECTS.PROJECT_DETAIL_PATH}/${this.activeProject?.id}`
      },
      {
        label: 'PROJECT_MODULE.PROJECT_VERSIONS_COMPONENT.PROJECT_VERSIONS',
        redirectUrl: PATH_DEFINITIONS.PROJECTS.PROJECT_VERSION_LIST
      },
      {
        label: this.projectVersion?.versionNumber
      }
    ];
  }

  onNavigateToProjectExportPage() {
    const exportPageURL = `${PATH_DEFINITIONS.PROJECTS.PROJECT_EXPORT}/${this.projectVersionId}`;
    this.routerStateService.navigate(exportPageURL);
  }

  onStatus(status) {
    this.status = status;
  }
}
